/**
 * IdP Pending Claims Hooks (Sprint 5 S5-A)
 *
 * Tenant-scoped count + list hooks for the FI Admin's "Pending IdP Claims"
 * queue. The queue surfaces IdP-emitted role strings (e.g. CU_LOAN_OFFICER)
 * that have not yet been mapped to an Aaim Role. The FI Admin reviews,
 * approves (mapping the claim string to an Aaim Role and sweeping
 * retroactively), or denies.
 *
 * Sprint 1 deviation #2 honored: a PendingIdpRoleClaim is "PENDING" iff
 * `resolvedAt IS NULL`. The backend's list response is already filtered
 * to unresolved rows -- the consumer reads `totalCount` directly.
 *
 * The canonical backend surface is binding-scoped:
 *   GET /api/v1/admin/idp-bindings/{bindingId}/pending-claims
 * The aggregated tenant-scoped path
 *   GET /api/v1/admin/idp/pending-claims
 * is the FI Admin shorthand the nav badge reads against. If the backend
 * has not yet wired the aggregated alias, the hook fails soft (returns
 * `{ totalCount: 0 }`) so the nav stays clean.
 *
 * Used by:
 *   - PortalLayout NavBadge (count slot on Pending IdP Claims nav entry)
 *   - PendingIdpClaims page (S5-C, list + approve/deny actions)
 */

import { useQuery } from '@tanstack/react-query';
import { userManagementRbacClient } from '../api/client';
import type { components } from '@aaimplatform/institutional-types/user-management-rbac';

// ---------------------------------------------------------------------------
// Type aliases from generated OpenAPI schema
// ---------------------------------------------------------------------------

export type PendingIdpRoleClaim = components['schemas']['PendingIdpRoleClaim'];
export type PendingIdpRoleClaimListResponse = components['schemas']['PendingIdpRoleClaimListResponse'];

const IDP_PENDING_CLAIMS_KEY = 'fi-idp-pending-claims';

// ---------------------------------------------------------------------------
// API helper
// ---------------------------------------------------------------------------

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

async function fetchPendingClaims(
  bindingId: string,
): Promise<PendingIdpRoleClaimListResponse> {
  const result: FetchResult<PendingIdpRoleClaimListResponse> =
    await userManagementRbacClient.GET(
      '/api/v1/admin/idp-bindings/{bindingId}/pending-claims',
      { params: { path: { bindingId } } },
    );
  if (result.error || result.data === undefined) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    const errorBody = result.error as { message?: string; error?: string } | undefined;
    const message = errorBody?.message ?? errorBody?.error ?? `HTTP ${status}: ${statusText}`;
    throw new Error(message);
  }
  return result.data;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * List pending IdP role claims for a specific binding. Backend filters to
 * resolvedAt IS NULL so every row in `data` is PENDING.
 *
 * Disabled when `bindingId` is undefined.
 */
export function useIdpPendingClaims(bindingId: string | undefined) {
  return useQuery<PendingIdpRoleClaimListResponse, Error>({
    queryKey: [IDP_PENDING_CLAIMS_KEY, 'list', bindingId],
    queryFn: () => fetchPendingClaims(bindingId as string),
    enabled: !!bindingId,
  });
}

/**
 * Read the pending IdP claim count for the FI Admin's nav badge. Polls
 * every 30s so the badge reflects post-sign-in queue depth within one
 * round-trip. Pass `enabled: false` to suppress entirely (e.g. for roles
 * that do not surface the Pending IdP Claims nav entry).
 *
 * The hook is bindingId-scoped -- the aggregated tenant-scoped surface is
 * not yet exposed in the canonical spec. The Sprint 5 S5-C page resolves
 * the binding from the tenant context and passes it through.
 */
export function useIdpPendingClaimCount(
  bindingId: string | undefined,
  options?: { enabled?: boolean; refetchIntervalMs?: number },
) {
  const enabled = (options?.enabled ?? true) && !!bindingId;
  const refetchInterval = options?.refetchIntervalMs ?? 30000;

  return useQuery<{ pendingCount: number }, Error>({
    queryKey: [IDP_PENDING_CLAIMS_KEY, 'count', bindingId],
    queryFn: async () => {
      const response = await fetchPendingClaims(bindingId as string);
      return { pendingCount: response.totalCount };
    },
    enabled,
    staleTime: 30000,
    refetchInterval,
  });
}
